
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SystemError {
  id: string;
  user_id: string | null;
  error_type: string;
  error_message: string;
  url: string | null;
  severity: string;
  resolved: boolean;
  created_at: string;
}

interface SystemErrorsMonitorProps {
  recentErrors: SystemError[];
  errorSummary?: {
    total_errors_today: number;
    total_errors_this_week: number;
    unresolved_errors: number;
    top_error_type: string | null;
    error_count: number;
  };
}

export const SystemErrorsMonitor: React.FC<SystemErrorsMonitorProps> = ({
  recentErrors,
  errorSummary,
}) => {
  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'destructive';
      case 'error':
        return 'destructive';
      case 'warning':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          System Error Monitor
        </CardTitle>
        <CardDescription>
          Real-time tracking of system errors and issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {errorSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {errorSummary.total_errors_today}
              </div>
              <div className="text-xs text-muted-foreground">Today</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {errorSummary.total_errors_this_week}
              </div>
              <div className="text-xs text-muted-foreground">This Week</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-800">
                {errorSummary.unresolved_errors}
              </div>
              <div className="text-xs text-muted-foreground">Unresolved</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">
                {errorSummary.top_error_type || 'None'}
              </div>
              <div className="text-xs text-muted-foreground">Top Error</div>
            </div>
          </div>
        )}

        {errorSummary && errorSummary.unresolved_errors > 10 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              High number of unresolved errors detected. Immediate attention may be required.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <h4 className="font-medium text-sm">Recent Errors</h4>
          {recentErrors.slice(0, 8).map((error) => (
            <div key={error.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant={getSeverityColor(error.severity)}>
                    {error.severity}
                  </Badge>
                  <span className="text-sm font-medium">{error.error_type}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(error.created_at), { addSuffix: true })}
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {error.error_message}
              </p>
              {error.url && (
                <p className="text-xs text-blue-600 truncate">
                  {error.url}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
